(() => {
  if (customElements.get('ales-craft-sync-v4')) return;
  customElements.define('ales-craft-sync-v4', class extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this._abort = new AbortController();
      const signal = this._abort.signal;
      this._steps = [...this.querySelectorAll('.am-step')];
      this._stage = this.querySelector('.am-stage');
      this._wide = matchMedia('(min-width:900px)');
      this._reduced = matchMedia('(prefers-reduced-motion:reduce)');
      this._inline = this._steps.map(step => step.querySelector('figure'));
      this._photos = this._inline.map(photo => {
        const copy = photo.cloneNode(true);
        copy.className = 'am-stage-photo';
        this._stage.append(copy);
        return copy;
      });
      this._active = 0;
      this._photos[0]?.classList.add('am-active');
      this._videos = [...this.querySelectorAll('video')];
      this._sync = () => {
        const linked = this.classList.contains('am-linked');
        this._videos.forEach(video => {
          const photo = video.closest('figure');
          const desktop = photo.classList.contains('am-stage-photo');
          const bounds = photo.getBoundingClientRect();
          const visible = !document.hidden && (linked ? desktop && photo.classList.contains('am-active') : !desktop)
            && bounds.bottom > 40 && bounds.top < innerHeight - 40 && bounds.width > 1;
          if (visible && !video.getAttribute('src')) {
            video.src = video.dataset.src;
            video.muted = true;
            video.load();
          }
          if (!visible) {
            video.pause();
            video._eligible = false;
          } else if (!video._eligible) {
            video._eligible = true;
            if (!this._reduced.matches) video.play().catch(() => {});
          }
        });
      };
      this._render = () => {
        this._frame = 0;
        if (this.classList.contains('am-linked')) {
          const header = document.querySelector('sticky-header,.shopify-section-header');
          const top = Math.max(24, Math.min(header?.getBoundingClientRect().height || 80, 180) + 16);
          this.style.setProperty('--am-top', top + 'px');
          const line = top + (innerHeight - top) * .45;
          let next = 0, distance = Infinity;
          this._steps.forEach((step, i) => {
            const bounds = step.getBoundingClientRect();
            const delta = Math.abs(bounds.top + bounds.height / 2 - line);
            if (delta < distance) { distance = delta; next = i; }
          });
          if (next !== this._active) {
            this._photos.forEach((photo, i) => {
              photo.classList.toggle('am-active', i === next);
              photo.inert = i !== next;
            });
            this._active = next;
          }
        }
        this._sync();
      };
      this._request = () => {
        if (!this._frame) this._frame = requestAnimationFrame(this._render);
      };
      this._setup = () => {
        this._observer?.disconnect();
        this._videos.forEach(video => { video.pause(); video._eligible = false; });
        const linked = this._wide.matches && !this._reduced.matches;
        this.classList.toggle('am-linked', linked);
        this._stage.inert = !linked;
        this._stage.setAttribute('aria-hidden', String(!linked));
        this._photos.forEach((photo, i) => { photo.inert = i !== this._active; });
        this._inline.forEach(photo => {
          photo.inert = linked;
          photo.setAttribute('aria-hidden', String(linked));
          photo.classList.remove('am-reveal', 'am-visible', 'am-inview');
        });
        if (!this._wide.matches && !this._reduced.matches && 'IntersectionObserver' in window) {
          const reveal = photo => {
            const image = photo.querySelector('img');
            if (photo.classList.contains('am-inview') && (!image || image.complete)) photo.classList.add('am-visible');
          };
          this._observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting && entry.intersectionRatio >= .12) {
                entry.target.classList.add('am-inview');
                reveal(entry.target);
              } else if (!entry.isIntersecting) {
                entry.target.classList.remove('am-inview', 'am-visible');
              }
            });
            this._request();
          }, {threshold: [0, .12], rootMargin: '0px 0px -40px 0px'});
          this._inline.forEach(photo => {
            photo.classList.add('am-reveal');
            const image = photo.querySelector('img');
            if (image && !image.complete) {
              const onReady = () => reveal(photo);
              image.addEventListener('load', onReady, {once: true, signal});
              image.addEventListener('error', onReady, {once: true, signal});
            }
            this._observer.observe(photo);
          });
        }
        this._request();
      };
      window.addEventListener('scroll', this._request, {passive: true, signal});
      window.addEventListener('resize', this._request, {signal});
      document.addEventListener('visibilitychange', this._request, {signal});
      this._wide.addEventListener('change', this._setup, {signal});
      this._reduced.addEventListener('change', this._setup, {signal});
      this._setup();
    }
    disconnectedCallback() {
      this._videos?.forEach(video => video.pause());
      this._abort?.abort();
      this._abort = null;
      this._observer?.disconnect();
      cancelAnimationFrame(this._frame);
      this._frame = 0;
      this._stage?.replaceChildren();
      this.classList.remove('am-linked');
      this._inline?.forEach(photo => {
        photo.inert = false;
        photo.removeAttribute('aria-hidden');
        photo.classList.remove('am-reveal', 'am-visible', 'am-inview');
      });
    }
  });
})();
